import {
    ArrowLeft,
    Graph,
    Scales,
    Table,
} from "@phosphor-icons/react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import ContentContainer from "../components/ContentContainer";
import MainLayout from "../components/MainLayout";
import Separator from "../components/Separator";
import IconButton from "../components/IconButton";
import Typography from "../components/Typography";
import {
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { SVGProps } from "react";
import ContextMenu from "../components/ContextMenu";
import { appPaths } from "../Routes";
import {
    FinancedEmissionsHistoryDataPointDto,
    useFinancedEmissionsHistory,
} from "../api/reports";

export const DotWithContextMenu = ({
    payload,
    value,
    sector,
    scopes,
    ...svgProps
}: {
    payload: FinancedEmissionsHistoryDataPointDto;
    value: number;
    sector: string;
    scopes: string;
} & SVGProps<SVGCircleElement>) => {
    const navigate = useNavigate();
    const { year } = payload;

    const menuItems = [
        {
            onClick: () => {
                return navigate(appPaths.reportsByYear(year.toString()));
            },
            label: "Report",
            icon: Table,
        },
        {
            onClick: () => {
                return navigate(
                    appPaths.calculationVeracity(sector, scopes, year.toString())
                );
            },
            label: "Origin",
            icon: Graph,
        },
        {
            onClick: () => {
                return undefined;
            },
            label: "Methodology",
            icon: Scales,
        },
    ];

    return (
        <ContextMenu
            component={"circle"}
            componentProps={{ ...svgProps, r: "6" }}
            menuItems={menuItems}
        />
    );
};

export const FinancedEmissionsHistoryPage = () => {
    const params = useParams();
    const [searchParams] = useSearchParams();

    const sector = params.sector as string;
    const yearParam = searchParams.get("year") ?? "2025";
    const scopeParams = searchParams.get("scopes") ?? "1,2,3";

    const { isLoading, financedEmissionsHistory, scopes } =
        useFinancedEmissionsHistory(
            sector,
            yearParam,
            scopeParams
        );

    const scopeDescription = `Scope ${scopes.join("/")} Financed Emissions`;

    return (
        <MainLayout isLoading={isLoading}>
            <ContentContainer className="pt-14 pb-11">
                <div className="flex flex-col sm:flex-row gap-6 items-center pb-5">
                    <div className="flex items-center">
                        <IconButton
                            label="Back"
                            icon={ArrowLeft}
                            iconSize="large"
                            className="mr-3"
                            onClick={() => window.history.back()}
                        />
                        <Typography variant="headline-small" component="h1">
                            {`${financedEmissionsHistory?.sectorName} ${scopeDescription} History`}
                        </Typography>
                    </div>
                </div>
                <Separator className="mb-6" />
                <ResponsiveContainer width={"100%"} minHeight={500}>
                    <LineChart
                        data={financedEmissionsHistory?.dataPoints}
                        margin={{
                            top: 5,
                            right: 30,
                            left: 20,
                            bottom: 5,
                        }}
                    >
                        <XAxis dataKey="year" />
                        <YAxis dataKey="financedEmissions" />
                        <Tooltip isAnimationActive={false} />
                        <Line
                            type="monotone"
                            dataKey="financedEmissions"
                            stroke="#8884d8"
                            activeDot={false}
                            isAnimationActive={false}
                            dot={(props) => {
                                delete props.dataKey;
                                return (
                                    <DotWithContextMenu
                                        {...props}
                                        sector={sector}
                                        scopes={scopeParams}
                                    />
                                );
                            }}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </ContentContainer>
        </MainLayout>
    );
};
